import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowRightLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Pencil,
  Plus,
  RotateCcw,
  Tags,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from './HelpHint';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import type { InventoryCategory } from '../lib/types';

/**
 * The categories on the stock list, as things somebody can manage.
 *
 * ## Why this screen exists
 *
 * The twelve categories were a TypeScript union, which meant the only way to
 * add one was a deployment, and there was no way at all to rename, reorder or
 * retire one. That is the wrong shape for a taxonomy. Every business on this
 * system groups its stock differently, and the person who discovers the list
 * has nowhere to put a duct rod is standing in front of the duct rod.
 *
 * ## Nothing here deletes anything
 *
 * Archiving is as far as it goes, and it is reversible from this same screen.
 * Items store the category's key, and job sheets years old were grouped by it;
 * a deleted row would leave every one of them naming nothing, and a report
 * grouping by category would quietly drop them rather than fail.
 *
 * That is also what makes the button safe to offer. An administrator who
 * archives the wrong category restores it, and has lost nothing in between.
 *
 * ## Who can do what, and why it is split
 *
 * `stock:edit_categories` — add, rename, reorder, move items between them.
 * Administrators and dispatchers. Adding an *item* is a fact about what was
 * fitted and a technician holds it; adding a *category* changes how the whole
 * business reports, and twelve people inventing "Poles", "Pole hardware" and
 * "Aerial" is exactly the fragmentation a list exists to prevent.
 *
 * `stock:manage` — archive and restore. Administrators only, because
 * archiving takes a category out of everybody's picker at once.
 */
export function CategoryManager() {
  const { can } = useSession();
  const mayEdit = can('stock:edit_categories');
  const mayArchive = can('stock:manage');
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ label: '', help: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState({ label: '', help: '' });
  const [movingId, setMovingId] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: api.inventoryCategories,
    enabled: open,
  });

  /*
   * Every picker in the application reads the categories, so all of them have
   * to hear about a change here — not just this screen. Forgetting one is how
   * somebody adds a category and then cannot find it on the form next door.
   */
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['inventory-categories'] });
    void queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-catalogue'] });
    void queryClient.invalidateQueries({ queryKey: ['material-catalogue'] });
  };

  const said = (message: string) => {
    setProblem(null);
    setOutcome(message);
    refresh();
  };

  const failed = (error: unknown, fallback: string) =>
    setProblem(error instanceof ApiError ? error.message : fallback);

  const add = useMutation({
    mutationFn: () =>
      api.addInventoryCategory({
        label: draft.label.trim(),
        ...(draft.help.trim() ? { help: draft.help.trim() } : {}),
      }),
    onSuccess: (category) => {
      setAdding(false);
      setDraft({ label: '', help: '' });
      said(`“${category.label}” is on the list.`);
    },
    onError: (error) => failed(error, 'Could not add that category.'),
  });

  const rename = useMutation({
    mutationFn: (id: string) =>
      api.editInventoryCategory(id, { label: edit.label.trim(), help: edit.help.trim() || null }),
    onSuccess: (category) => {
      setEditingId(null);
      said(`“${category.label}” saved. Nothing was re-filed.`);
    },
    onError: (error) => failed(error, 'Could not save that change.'),
  });

  const reorder = useMutation({
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) =>
      api.editInventoryCategory(id, { sortOrder }),
    onSuccess: () => {
      setProblem(null);
      refresh();
    },
    onError: (error) => failed(error, 'Could not reorder that.'),
  });

  const move = useMutation({
    mutationFn: ({ id, toId }: { id: string; toId: string }) =>
      api.moveInventoryCategoryItems(id, toId),
    onSuccess: (result) => {
      setMovingId(null);
      setMoveTo('');
      said(result.message);
    },
    onError: (error) => failed(error, 'Could not move those items.'),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.archiveInventoryCategory(id),
    onSuccess: (result) => said(result.message),
    onError: (error) => failed(error, 'Could not archive that category.'),
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.restoreInventoryCategory(id),
    onSuccess: (result) => said(result.message),
    onError: (error) => failed(error, 'Could not restore that category.'),
  });

  const all = list.data?.categories ?? [];
  const live = all.filter((one) => !one.isArchived);
  const archived = all.filter((one) => one.isArchived);
  const shown = showArchived ? archived : live;

  const startEditing = (category: InventoryCategory) => {
    setEditingId(category.id);
    setEdit({ label: category.label, help: category.help ?? '' });
    setMovingId(null);
  };

  /** Swap with the neighbour above or below, by sort order. */
  const nudge = (category: InventoryCategory, direction: -1 | 1) => {
    const index = live.findIndex((one) => one.id === category.id);
    const swapWith = live[index + direction];
    if (!swapWith) return;
    reorder.mutate({ id: category.id, sortOrder: swapWith.sortOrder });
    reorder.mutate({ id: swapWith.id, sortOrder: category.sortOrder });
  };

  return (
    <section className="card space-y-3" data-categories>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button
          type="button"
          className="flex items-center gap-1.5 text-left font-heading text-lg"
          onClick={() => setOpen((was) => !was)}
          aria-expanded={open}
        >
          <Tags className="h-5 w-5 text-primary-600" aria-hidden="true" />
          Categories
          {open ? (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          )}
        </button>

        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          How the list is grouped
          <HelpHint>
            The headings the stock list is grouped under, on the technician’s checklist and in
            every report.
            <br />
            <br />
            You can add your own, rename any of them, and put them in the order that suits how you
            work. Renaming is safe: items store a hidden key rather than the name, so correcting a
            category’s wording never re-files anything.
            <br />
            <br />
            <strong>Nothing here is ever deleted.</strong> Archiving takes a category out of every
            picker and keeps every record that used it readable — and you can restore it whenever
            you like.
          </HelpHint>
        </span>
      </div>

      {!open && (
        <p className="text-sm text-muted-foreground">
          {mayEdit
            ? 'Add your own, rename them, reorder them, or archive one you no longer use.'
            : 'Open to see how the stock list is grouped.'}
        </p>
      )}

      {open && (
        <>
          {outcome && (
            <p className="flex items-start justify-between gap-3 rounded-xl border border-secondary-300 bg-secondary-50 p-3 text-sm text-secondary-900">
              <span>{outcome}</span>
              <button type="button" onClick={() => setOutcome(null)} aria-label="Dismiss">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </p>
          )}

          {problem && (
            <p className="rounded-xl border border-accent-300 bg-accent-50 p-3 text-sm text-accent-900">
              {problem}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {mayEdit && !adding && !showArchived && (
              <button type="button" className="btn-secondary" onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add a category
              </button>
            )}

            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => {
                setShowArchived((was) => !was);
                setAdding(false);
              }}
            >
              <Archive className="h-4 w-4" aria-hidden="true" />
              {showArchived ? `Back to the list (${live.length})` : `Archived (${archived.length})`}
            </button>
          </div>

          {adding && (
            <div className="space-y-2 rounded-xl border border-border p-3">
              <label className="block text-sm">
                <span className="mb-1 flex items-center gap-1 font-medium">
                  Name
                  <HelpHint>
                    What this group of things is called on screen — “Aerial hardware”, “Splicing &
                    jointing”.
                    <br />
                    <br />
                    Name it by what the things are <em>for</em> rather than what they are made of,
                    because that is how somebody looks for them at a cabinet. You can change this
                    later without re-filing anything.
                  </HelpHint>
                </span>
                <input
                  className="input w-full"
                  value={draft.label}
                  onChange={(event) => setDraft((old) => ({ ...old, label: event.target.value }))}
                  placeholder="Aerial hardware"
                />
              </label>

              <label className="block text-sm">
                <span className="mb-1 flex items-center gap-1 font-medium">
                  What belongs in it
                  <HelpHint>
                    One line, shown under the picker and in the help on the add-item form.
                    <br />
                    <br />
                    Worth writing. A category nobody can define gets picked by position, and a year
                    later half the stock is in the wrong group with no way to tell which half.
                  </HelpHint>
                </span>
                <input
                  className="input w-full"
                  value={draft.help}
                  onChange={(event) => setDraft((old) => ({ ...old, help: event.target.value }))}
                  placeholder="Poles, stays, clamps, brackets — what gets the cable through the air."
                />
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={add.isPending}
                  onClick={() =>
                    draft.label.trim().length < 3
                      ? setProblem('Give the category a name of at least three characters.')
                      : add.mutate()
                  }
                >
                  {add.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  )}
                  Add it
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setAdding(false);
                    setProblem(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {list.isPending && (
            <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-primary-500" />
          )}

          {list.isError && (
            <p className="text-sm text-muted-foreground">
              The categories could not be loaded. Reload the page, and if it keeps happening tell an
              administrator.
            </p>
          )}

          {!list.isPending && shown.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {showArchived
                ? 'Nothing has been archived. Anything you archive lands here and can be brought back.'
                : 'No categories yet.'}
            </p>
          )}

          <ul className="divide-y divide-border">
            {shown.map((category, index) => (
              <li key={category.id} className="py-2.5">
                {editingId === category.id ? (
                  <div className="space-y-2">
                    <input
                      className="input w-full"
                      value={edit.label}
                      onChange={(event) => setEdit((old) => ({ ...old, label: event.target.value }))}
                      aria-label={`Name for ${category.label}`}
                    />
                    <input
                      className="input w-full text-sm"
                      value={edit.help}
                      onChange={(event) => setEdit((old) => ({ ...old, help: event.target.value }))}
                      placeholder="What belongs in it"
                      aria-label={`What belongs in ${category.label}`}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn-primary py-1.5 text-sm"
                        disabled={rename.isPending}
                        onClick={() =>
                          edit.label.trim().length < 3
                            ? setProblem('Give the category a name of at least three characters.')
                            : rename.mutate(category.id)
                        }
                      >
                        <Check className="h-4 w-4" aria-hidden="true" />
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn-secondary py-1.5 text-sm"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2 font-medium">
                        {category.label}
                        {category.isBuiltIn && (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                            built in
                          </span>
                        )}
                        {category.isArchived && (
                          <span className="rounded-full bg-accent-50 px-2 py-0.5 text-xs font-normal text-accent-800">
                            archived
                          </span>
                        )}
                      </p>
                      {category.help && (
                        <p className="mt-0.5 text-sm text-muted-foreground">{category.help}</p>
                      )}
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {category.itemCount === 0
                          ? 'Nothing filed under it'
                          : `${category.itemCount} ${category.itemCount === 1 ? 'item' : 'items'}`}
                        {category.isArchived && category.archivedByLabel
                          ? ` · archived by ${category.archivedByLabel}`
                          : ''}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      {!category.isArchived && mayEdit && (
                        <>
                          <button
                            type="button"
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted disabled:opacity-30"
                            title={index === 0 ? 'Already first' : 'Move up'}
                            disabled={index === 0 || reorder.isPending}
                            onClick={() => nudge(category, -1)}
                          >
                            <ChevronUp className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Move {category.label} up</span>
                          </button>
                          <button
                            type="button"
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted disabled:opacity-30"
                            title={index === shown.length - 1 ? 'Already last' : 'Move down'}
                            disabled={index === shown.length - 1 || reorder.isPending}
                            onClick={() => nudge(category, 1)}
                          >
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Move {category.label} down</span>
                          </button>
                          <button
                            type="button"
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                            title="Rename, or change what it says"
                            onClick={() => startEditing(category)}
                          >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Rename {category.label}</span>
                          </button>
                          <button
                            type="button"
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted disabled:opacity-30"
                            title={
                              category.ownItemCount === 0
                                ? 'Nothing of yours is filed under this one to move'
                                : 'Move its items into another category'
                            }
                            disabled={category.ownItemCount === 0}
                            onClick={() => {
                              setMovingId(movingId === category.id ? null : category.id);
                              setMoveTo('');
                              setEditingId(null);
                            }}
                          >
                            <ArrowRightLeft className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">Move items out of {category.label}</span>
                          </button>
                        </>
                      )}

                      {!category.isArchived && mayArchive && (
                        <button
                          type="button"
                          className="rounded-lg p-2 text-muted-foreground hover:bg-muted disabled:opacity-30"
                          title={
                            category.itemCount > 0
                              ? `Move its ${category.itemCount} items out first`
                              : 'Archive it — nothing is deleted and you can restore it'
                          }
                          disabled={category.itemCount > 0 || archive.isPending}
                          onClick={() => archive.mutate(category.id)}
                        >
                          <Archive className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">Archive {category.label}</span>
                        </button>
                      )}

                      {category.isArchived && mayArchive && (
                        <button
                          type="button"
                          className="btn-secondary py-1.5 text-sm"
                          disabled={restore.isPending}
                          onClick={() => restore.mutate(category.id)}
                        >
                          <RotateCcw className="h-4 w-4" aria-hidden="true" />
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {movingId === category.id && (
                  <div className="mt-2 flex flex-wrap items-end gap-2 rounded-xl border border-border p-3">
                    <label className="block flex-1 text-sm">
                      <span className="mb-1 block font-medium">
                        Move {category.ownItemCount} of your items into
                      </span>
                      <select
                        className="input w-full"
                        value={moveTo}
                        onChange={(event) => setMoveTo(event.target.value)}
                      >
                        <option value="">Choose a category…</option>
                        {live
                          .filter((one) => one.id !== category.id)
                          .map((one) => (
                            <option key={one.id} value={one.id}>
                              {one.label}
                            </option>
                          ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={move.isPending}
                      onClick={() =>
                        moveTo
                          ? move.mutate({ id: category.id, toId: moveTo })
                          : setProblem('Choose the category to move them into.')
                      }
                    >
                      {move.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <ArrowRightLeft className="h-4 w-4" aria-hidden="true" />
                      )}
                      Move them
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setMovingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          {!mayEdit && (
            <p className="text-xs text-muted-foreground">
              Read-only for your role. A dispatcher or an administrator can change these.
            </p>
          )}
        </>
      )}
    </section>
  );
}
